'use client';

import { usePathname } from 'next/navigation';
import { useBuilderReady } from '@/hooks/useBuilderReady';
import useClientStore from '@/store/useClientStore';
import { usaNavbarInline } from '@/lib/navbarPorTenant';
import Navbar from './Navbar';

/**
 * Rutas que NO se arman en el builder: no traen navbar propio en ningún tenant,
 * así que siempre necesitan el del layout. `/embed` se resuelve antes, aparte.
 */
const RUTAS_SIN_BUILDER = ['/vehicles', '/builder'];

/**
 * Navbar como fuente única. El dealer configura SU navbar en la home (dentro del
 * builder). Ese navbar se muestra en todo el sitio vía el Navbar estático del
 * layout, que lee los links/CTA configurados desde pages.home (extractBuilderNav).
 *
 * Solo la home renderiza su navbar inline (para editarlo WYSIWYG en el builder);
 * en el resto de páginas usamos el Navbar del layout. Antes cada página secundaria
 * traía su propia copia del navbar horneada en su árbol, que quedaba
 * desincronizada (o ausente) cuando el dealer editaba la home. El nodo navbar de
 * las páginas no-home se elimina en getPageBuilderData para que no salga duplicado.
 *
 * Mientras la estructura del builder aún carga renderizamos NADA en vez del navbar
 * estático, para evitar el flash de defaults hardcodeados. Ver useBuilderReady.
 */
export default function ConditionalNavbar() {
  const pathname = usePathname();
  const status = useBuilderReady();
  const { client } = useClientStore();

  // Embed pages always use the static navbar
  if (pathname?.startsWith('/embed')) {
    return <Navbar />;
  }

  // Builder client still hydrating elements_structure → wait, don't flash static
  if (status === 'loading') return null;

  // Non-builder client, or structure failed to load (timeout) → static navbar.
  // Va ANTES de la excepción por tenant a propósito: si el árbol no cargó, el
  // navbar del layout es la única red que queda.
  if (status === 'fallback') return <Navbar />;

  // Tenants que traen el navbar dentro del árbol de CADA página del builder (ver
  // `lib/navbarPorTenant`): montar además el del layout lo duplicaría y, como es
  // un overlay fijo, taparía lo que la página pone arriba de su propio navbar.
  // El catálogo y demás rutas sin builder lo siguen usando.
  if (
    usaNavbarInline(client?.id) &&
    !RUTAS_SIN_BUILDER.some((p) => pathname?.startsWith(p))
  ) {
    return null;
  }

  // status === 'ready': solo la home renderiza su navbar inline (editable en el
  // builder). El resto de páginas usan el Navbar del layout, que lee el navbar
  // configurado en la home → fuente única, consistente en todo el sitio.
  if (pathname === '/') return null;

  return <Navbar />;
}
